import React, { useEffect, useState, useCallback } from 'react';
import { FeatureNode } from '../models/uiFeatureConfig';

/** Walk a subtree and collect the guids whose `selected` flag is true. */
function collectSelected(nodes: FeatureNode[] | undefined, acc: Set<string>): void {
  if (!nodes) return;
  for (const n of nodes) {
    if (n.selected) acc.add(n.guid);
    if (n.items && n.items.length) collectSelected(n.items, acc);
  }
}

/** Collect a node's own guid plus every descendant guid beneath it. */
function collectDescendantGuids(node: FeatureNode, acc: string[] = []): string[] {
  acc.push(node.guid);
  if (node.items && node.items.length) {
    node.items.forEach(child => collectDescendantGuids(child, acc));
  }
  return acc;
}

const TreeNode: React.FC<{
  node: FeatureNode;
  selected: Set<string>;
  onToggle: (node: FeatureNode, checked: boolean) => void;
}> = ({ node, selected, onToggle }) => {
  const hasChildren = !!(node.items && node.items.length);
  const [open, setOpen] = useState(true); // expandNodesRecursive = true

  return (
    <div className="ft-node">
      <div className="ft-row">
        {hasChildren ? (
          <span className="ft-toggle" onClick={() => setOpen(o => !o)}>{open ? '▾' : '▸'}</span>
        ) : (
          <span className="ft-toggle-spacer" />
        )}
        <input
          type="checkbox"
          className="ft-checkbox"
          checked={selected.has(node.guid)}
          onChange={e => onToggle(node, e.target.checked)}
        />
        <span className="ft-label" onClick={() => onToggle(node, !selected.has(node.guid))}>
          {node.text}
        </span>
      </div>
      {hasChildren && open && (
        <div className="ft-children">
          {node.items!.map(child => (
            <TreeNode key={`${child.guid}_${child.id}`} node={child} selected={selected} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  );
};

export const FeatureTree: React.FC<{
  /** Module tree, pre-marked with `selected` flags for the current config. */
  nodes: FeatureNode[];
  /** Stable identity that changes when the module/size reloads (module short name + size). */
  resetKey: string;
  /** Fires with the full list of selected guids after a user toggle. */
  onSelectionChange: (selectedGuids: string[]) => void;
}> = ({ nodes, resetKey, onSelectionChange }) => {
  const [selected, setSelected] = useState<Set<string>>(() => {
    const s = new Set<string>();
    collectSelected(nodes, s);
    return s;
  });

  useEffect(() => {
    const s = new Set<string>();
    collectSelected(nodes, s);
    setSelected(s);
  }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = useCallback((node: FeatureNode, checked: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      const affectedGuids = collectDescendantGuids(node);
      affectedGuids.forEach(guid => {
        if (checked) next.add(guid);
        else next.delete(guid);
      });
      onSelectionChange(Array.from(next));
      return next;
    });
  }, [onSelectionChange]);

  return (
    <>
      {nodes.map(node => (
        <TreeNode key={`${node.guid}_${node.id}`} node={node} selected={selected} onToggle={handleToggle} />
      ))}
    </>
  );
};
